import { useCallback, useEffect, useState } from "react";
import { KanbanSquare, List, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { FuBadge, QuickActions, fmtINR } from "./CrmShared";
import CrmPipelines from "./CrmPipelines";

export default function CrmPipeline({ refreshKey }) {
  const [leads, setLeads] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [pipeId, setPipeId] = useState("");
  const [view, setView] = useState("kanban");
  const [manage, setManage] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState("");

  const load = useCallback(() => {
    api.get("/store-admin/crm/leads", { params: { limit: 500, sort: "updated_at" } })
      .then((r) => setLeads(r.data.leads)).catch((e) => toast.error(errMsg(e)));
    api.get("/store-admin/crm/pipelines").then((r) => setPipelines(r.data.pipelines)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const pipeline = pipelines.find((p) => p.id === pipeId) || pipelines.find((p) => p.is_default) || { stages: [] };
  const stages = pipeline.stages || [];
  const visible = leads.filter((l) => (pipeId ? (l.pipeline_id || "") === pipeId : (!l.pipeline_id || l.pipeline_id === pipeline.id)));

  const move = async (l, stageKey) => {
    if (stageKey === l.stage) return;
    try {
      await api.put(`/store-admin/crm/leads/${l.id}`, { stage: stageKey, pipeline_id: pipeline.id || "" });
      toast.success(`${l.name} → ${stages.find((s) => s.key === stageKey)?.name || stageKey}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const stageBadge = (key) => {
    const s = stages.find((x) => x.key === key);
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide text-white"
        style={{ backgroundColor: s?.color || "#64748b" }} data-testid={`stage-${key}`}>
        {s?.name || key}
      </span>
    );
  };

  const Card = ({ l }) => (
    <div draggable onDragStart={(e) => { setDragId(l.id); e.dataTransfer.effectAllowed = "move"; }}
      className="rounded-xl border border-border bg-background p-3 space-y-2 shadow-sm cursor-grab active:cursor-grabbing" data-testid={`pipe-card-${l.id}`}>
      <p className="font-bold text-sm truncate">{l.name}</p>
      {l.company && <p className="text-xs text-muted-foreground truncate">{l.company}</p>}
      <div className="flex items-center justify-between gap-2">
        {l.value > 0 ? <span className="text-sm font-extrabold">{fmtINR(l.value)}</span> : <span className="text-xs text-muted-foreground">—</span>}
        <QuickActions name={l.name} phone={l.phone} whatsapp={l.whatsapp} email={l.email} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <FuBadge lead={l} />
        {l.assigned_to && <span className="text-[10px] font-bold text-muted-foreground">@{l.assigned_to}</span>}
      </div>
      <select value={l.stage} onChange={(e) => move(l, e.target.value)} data-testid={`pipe-move-${l.id}`}
        className="w-full h-8 rounded-lg border border-input bg-background px-2 text-xs font-bold">
        {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
      </select>
    </div>
  );

  if (manage) return <CrmPipelines refreshKey={refreshKey} onChanged={() => {}} />;

  return (
    <div className="space-y-4" data-testid="crm-pipeline">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <select value={pipeline.id || ""} onChange={(e) => setPipeId(e.target.value)} data-testid="pipeline-selector"
          className="h-10 rounded-xl border border-input bg-background px-3 text-sm font-bold">
          {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={() => setManage(true)} data-testid="pipeline-manage"
            className="h-9 px-4 rounded-full text-xs font-extrabold border border-border flex items-center gap-1.5 hover:bg-muted">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Manage Pipelines
          </button>
          <button onClick={() => setView("kanban")} data-testid="pipeline-kanban-view"
            className={`h-9 px-4 rounded-full text-xs font-extrabold border flex items-center gap-1.5 ${view === "kanban" ? "bg-foreground text-background border-foreground" : "border-border"}`}>
            <KanbanSquare className="h-3.5 w-3.5" /> Kanban
          </button>
          <button onClick={() => setView("list")} data-testid="pipeline-list-view"
            className={`h-9 px-4 rounded-full text-xs font-extrabold border flex items-center gap-1.5 ${view === "list" ? "bg-foreground text-background border-foreground" : "border-border"}`}>
            <List className="h-3.5 w-3.5" /> List
          </button>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="flex gap-3 overflow-x-auto pb-3 items-start" data-testid="pipeline-board">
          {stages.map((s) => {
            const items = visible.filter((l) => l.stage === s.key);
            const val = items.reduce((a, l) => a + (l.value || 0), 0);
            return (
              <div key={s.key}
                onDragOver={(e) => { e.preventDefault(); setDragOver(s.key); }}
                onDragLeave={() => setDragOver("")}
                onDrop={(e) => { e.preventDefault(); setDragOver(""); const l = leads.find((x) => x.id === dragId); if (l) move(l, s.key); }}
                className={`w-[270px] shrink-0 rounded-2xl border p-3 transition-colors ${dragOver === s.key ? "border-emerald-500 bg-emerald-50/50" : "border-border bg-muted/30"}`}
                data-testid={`pipe-col-${s.key}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  {stageBadge(s.key)}
                  <span className="text-[11px] font-extrabold text-muted-foreground">{items.length}{val > 0 ? ` · ${fmtINR(val)}` : ""}</span>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {items.map((l) => <Card key={l.id} l={l} />)}
                  {!items.length && <p className="text-[11px] text-muted-foreground text-center py-4">Drop leads here</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2" data-testid="pipeline-list">
          {visible.map((l) => (
            <div key={l.id} className="rounded-2xl border border-border bg-background p-4 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[150px]">
                <p className="font-bold text-sm truncate">{l.name}{l.company ? ` · ${l.company}` : ""}</p>
                <p className="text-xs text-muted-foreground">{[l.phone, l.city].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              {stageBadge(l.stage)}<FuBadge lead={l} />
              {l.value > 0 && <span className="text-sm font-extrabold">{fmtINR(l.value)}</span>}
              {l.assigned_to && <span className="text-[11px] font-bold text-muted-foreground">@{l.assigned_to}</span>}
              <select value={l.stage} onChange={(e) => move(l, e.target.value)} data-testid={`list-move-${l.id}`}
                className="h-8 rounded-lg border border-input bg-background px-2 text-xs font-bold">
                {stages.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
              </select>
            </div>
          ))}
          {!visible.length && <p className="text-sm text-muted-foreground text-center py-10">No leads in this pipeline yet.</p>}
        </div>
      )}
    </div>
  );
}
